// PageController.jsx
import React, { useContext, useEffect, useState, memo } from "react";
import { DataContext } from "./GameContext";
import LobbyPage from "./pages/LobbyPage";
import LoginPage from "./pages/Login";
import ConnectingPage from "./pages/ConnectingPage";
import PreGamePage from "./pages/PreGamePage";
import Modal from "./components/Modal";
import IntruderPage from "./pages/IntruderPage";
import CrewmemberPage from "./pages/CrewPage";
import ProgressBar from "./components/ProgressBar";
import "./signal/setup.css";
import MeetingWaitingPage from "./pages/MeetingWaitingPage";
import DeadPage from "./pages/DeadPage";
import HackedPage from "./pages/HackedPage";
import GameRunningPage from "./pages/GameRunning";
import ReactorMeltdown from "./pages/MeltdownPage";
import ReactorNormal from "./pages/ReactorPage";
import { isMobile as isMobileDevice } from "react-device-detect";
import MeltdownInfo from "./pages/MeltdownInfo";
import CrewVictoryScreen from "./pages/CrewVictory";
import IntruderVictoryScreen from "./pages/IntruderVictory";
import NuclearMeltdownScreen from "./pages/MeltdownEnd";
import ReactorWaiting from "./pages/ReactorWaiting";
import TaskEntryPage from "./pages/TaskEntryPage";
import VotingPage from "./pages/VotingPage";
import MeetingResultPage from "./pages/MeetingResultsPage";
import GameConfigPage from "./pages/GameConfigPage";
import WaitingNextRoundPage from "./pages/WaitingNextRoundPage";

// Screens that still lean on the Signal setup flatten layer (see signal/setup.css): the
// reactor's desktop home and the selfie camera on the join screen. The other setup screens
// (connecting, game config, lobby, task entry) are drawn in Signal markup directly.
const SETUP_PAGES = new Set(["lobby", "login"]);

// Allow URL param override for testing: ?mobile=true or ?mobile=false
const urlParams = new URLSearchParams(window.location.search);
const mobileOverride = urlParams.get('mobile');
const isMobile = mobileOverride !== null ? mobileOverride === 'true' : isMobileDevice;

const PageController = () => {
    const {
        playerState,
        connected,
        running,
        crewScore,
        meetingState,
        taskGoal,
        hackTime,
        meltdownCode,
        meltdownTimer,
        endState,
        taskEntry,
        showSusPage,
        setShowSusPage,
        inRoom,
        roomCode,
        isRoomCreator,
        roomOpen,
        taskCreationMode,
        waitingForNextRound,
        roundRunningAtEntry,
    } = useContext(DataContext);

    const [currentPage, setCurrentPage] = useState("connecting");

    useEffect(() => {
        if (!connected) {
            // A host mid-setup keeps their choices on screen while the connection comes back.
            setCurrentPage(page => (page === "gameConfig" ? page : "connecting"));
            return;
        }

        // Show lobby if not in a room yet
        if (!inRoom && !roomCode) {
            setCurrentPage("lobby");
            return;
        }

        // Show config page for room creator before room is opened
        if (inRoom && isRoomCreator && !roomOpen && !running) {
            setCurrentPage("gameConfig");
            return;
        }

        // Joined mid-round: wait out this round (meetings, meltdowns and the result included).
        if (isMobile && waitingForNextRound) {
            setCurrentPage("waitingNextRound");
            return;
        }

        // Came in while a round was on and hasn't picked a name yet: they can still join the
        // waiting list, whatever the round is doing meanwhile.
        if (isMobile && roundRunningAtEntry && !(playerState?.playerId || playerState?.player_id)) {
            setCurrentPage("login");
            return;
        }

        // Phones finish reading the vote result first (they tap Continue); the reactor has no
        // result screen, so it goes straight to the end screen.
        if (endState && (!isMobile || meetingState?.stage !== 'over')) {
            setCurrentPage(endState)
            return;
        }

        if (!isMobile) {
            if(!running) {
                setCurrentPage("reactorWaiting");
                return;
            }
            if (meltdownTimer > 0) {
                setCurrentPage("meltdown");
            } else {
                setCurrentPage("reactorNormal");
            }
            return;
        }

        if (running && !playerState?.username) {
            setCurrentPage("gameRunning");
            return;
        }

        // if (taskEntry) {
        //     console.log("task entry")
        //     setCurrentPage("taskEntry")
        //     return;
        // }

        if ((!playerState?.username || !(playerState?.playerId || playerState?.player_id)) && !running) {
            setCurrentPage("login");
            return;
        }

        if (playerState && !running) {
            setCurrentPage("players");
            return;
        }

        if (playerState?.alive === false && meetingState?.stage !== 'over') {
            setCurrentPage("dead");
            return;
        }

        if (meltdownCode) {
            setCurrentPage("meltdownCode");
            return;
        }

        if (hackTime > 0) {
            setCurrentPage("hacked");
            return;
        }

        if (meetingState) {
            console.log("MEETING STATE")
            console.log(meetingState.stage)
            if (meetingState.stage === 'waiting') {
                setCurrentPage("meetingWaiting");
                return;
            } else if (meetingState.stage === 'voting') {
                console.log("players page")
                setCurrentPage("votingPage");
                return;
            } else if (meetingState.stage === 'over') {
                setCurrentPage("resultsPage");
                return;
            }

        }

        if (running) {
            console.log(showSusPage)
            if (showSusPage) {
                setCurrentPage("intruder");
                return;
            } else {
                setCurrentPage("crewmember");
                return;
            }
        }

        setCurrentPage("unknown");
    }, [
        connected,
        isMobile,
        meltdownTimer,
        running,
        playerState,
        meltdownCode,
        hackTime,
        meetingState,
        endState,
        taskEntry,
        showSusPage,
        inRoom,
        roomCode,
        isRoomCreator,
        roomOpen,
        waitingForNextRound,
        roundRunningAtEntry,
    ]);

    const pages = {
        connecting: <ConnectingPage />,
        lobby: <LobbyPage />,
        gameConfig: <GameConfigPage />,
        meltdown: <ReactorMeltdown />,
        reactorNormal: <ReactorNormal />,
        gameRunning: <GameRunningPage />,
        login: <LoginPage />,
        players: <PreGamePage />,
        dead: <DeadPage />,
        meltdownCode: <MeltdownInfo />,
        hacked: <HackedPage hackTime={hackTime} />,
        meetingWaiting: <MeetingWaitingPage />,
        votingPage: <VotingPage />,
        resultsPage: <MeetingResultPage />,
        intruder: <IntruderPage setShowSusPage={setShowSusPage}/>,
        crewmember: <CrewmemberPage setShowSusPage={setShowSusPage} />,
        sus_victory: <IntruderVictoryScreen />,
        victory: <CrewVictoryScreen />,
        meltdown_fail: <NuclearMeltdownScreen />,
        reactorWaiting: <ReactorWaiting />,
        taskEntry: <TaskEntryPage />,
        waitingNextRound: <WaitingNextRoundPage />,
        unknown: (
            <p>
                You're really not supposed to see this... Uhhh please go talk to
                Sam and tell him he fucked up somewhere
            </p>
        ),
    };

    return (
        <div>
            {/* Round pop-ups (meltdown over, last stand...) aren't for phones that are not in the round */}
            {currentPage !== "waitingNextRound" && !(currentPage === "login" && roundRunningAtEntry) && <Modal />}
            {running && isMobile && !endState && currentPage !== "waitingNextRound" && (
                <ProgressBar
                    score={crewScore}
                    goalScore={taskGoal}
                    sus={playerState?.sus}
                />
            )}
            {SETUP_PAGES.has(currentPage)
                ? <div className="sg-setup">{pages[currentPage]}</div>
                : pages[currentPage]}
        </div>
    );
};

export default memo(PageController);
